import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../lib/api';
import type { AnalyticsResponse } from '../types';
import { formatMoney } from '../lib/format';
import { MaterialSymbol } from '../components/icons';
import SetupCard from '../components/SetupCard';

/**
 * "Unified Analytics & Storefront Dashboard" — the design's layout and icons
 * with live data:
 * - chat metrics come from our DB (GET /api/admin/analytics → chat)
 * - sales metrics are Shopify-order attribution (→ sales); when the token
 *   lacks read_orders the sales blocks show "—" plus a re-auth hint.
 */

const RING_CIRCUMFERENCE = 2 * Math.PI * 34;
const INQUIRY_COLORS = [
  '#4a52ff',
  'var(--cg-tertiary)',
  'var(--cg-secondary)',
  'var(--cg-outline-variant)',
];

function pct(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}

function changeBadge(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const rounded = Math.round(value * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function money(amount: number | null | undefined, currency: string | null): string {
  if (amount === null || amount === undefined || !currency) return '—';
  return formatMoney(amount, currency);
}

function MetricCard({
  label,
  value,
  icon,
  iconTone,
  badge,
  badgeTone,
  highlight,
}: {
  label: string;
  value: string;
  icon: string;
  iconTone?: 'tertiary' | 'secondary';
  badge: string | null;
  badgeTone: 'up' | 'down' | 'plain' | 'highlight';
  highlight?: boolean;
}): ReactNode {
  const badgeClass =
    badgeTone === 'up' ? 'is-up' : badgeTone === 'down' ? 'is-down' : badgeTone === 'plain' ? 'is-plain' : '';
  return (
    <div className={`cg-card cg-stat-card${highlight ? ' is-highlight' : ''}`}>
      <div className="cg-stat-toprow">
        <div className={`cg-stat-icon${iconTone ? ` is-${iconTone}` : ''}`}>
          <MaterialSymbol name={icon} />
        </div>
        {badge ? <span className={`cg-trend ${badgeClass}`}>{badge}</span> : null}
      </div>
      <div>
        <div className="cg-stat-label">{label}</div>
        <div className="cg-stat-value">{value}</div>
      </div>
    </div>
  );
}

export default function DashboardPage(): ReactNode {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;

    const load = (initial: boolean) => {
      if (initial) setLoading(true);
      api
        .analytics()
        .then((a) => {
          if (!cancelled) setData(a);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 402) {
            setError('Upgrade to PRO to view chat analytics.');
          } else if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('Failed to load analytics');
          }
        })
        .finally(() => {
          if (!cancelled && initial) setLoading(false);
        });
    };

    load(true);
    // Sales attribution hits the Shopify API — poll gently.
    const interval = setInterval(() => load(false), 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const chat = data?.chat ?? null;
  const sales = data?.sales ?? null;
  const totals = chat?.totals ?? { total: 0, open: 0, closed: 0 };
  const resolutionRate = totals.total > 0 ? totals.closed / totals.total : null;
  const weekChange = chat?.weekChangePct ?? null;
  const currency = sales?.currencyCode ?? null;
  const maxVolume = Math.max(1, ...(chat?.volume ?? []).map((d) => d.total));

  const chatConv = sales?.chatConversionRate ?? null;
  const siteConv = sales?.siteConversionRate ?? null;
  const maxConv = Math.max(chatConv ?? 0, siteConv ?? 0);
  const convRatio =
    chatConv !== null && siteConv !== null && siteConv > 0 ? chatConv / siteConv : null;

  const aovChat = sales?.aovChat ?? null;
  const aovStore = sales?.aovStore ?? null;
  const aovUplift =
    aovChat !== null && aovStore !== null && aovStore > 0
      ? Math.round(((aovChat - aovStore) / aovStore) * 100)
      : null;

  const recovery = sales?.cartRecovery ?? null;
  const ringOffset = RING_CIRCUMFERENCE * (1 - (recovery?.rate ?? 0));

  const salesUnavailable = data !== null && sales !== null && !sales.available;
  const inquiries = chat?.inquiries ?? [];
  const hasInquiryData = inquiries.some((i) => i.pct > 0);
  const topProducts = sales?.topProducts ?? [];

  return (
    <main className="cg-content">
      <div className="cg-page-head">
        <h1 className="cg-page-title">Analytics Dashboard</h1>
      </div>

      <SetupCard style={{ marginBottom: 24 }} />

      {error ? <div className="cg-banner cg-banner-warning">{error}</div> : null}
      {salesUnavailable ? (
        <div className="cg-banner cg-banner-warning">
          Sales metrics need the <strong>read_orders</strong> permission. Reinstall ChatForge
          from the Shopify App Store listing to grant it — chat metrics below are live either
          way.
        </div>
      ) : null}

      {/* High-level metric cards */}
      <div className="cg-stat-grid">
        <MetricCard
          label="Total Conversations"
          value={loading ? '—' : totals.total.toLocaleString()}
          icon="forum"
          badge={chat ? (changeBadge(weekChange) ?? 'New') : null}
          badgeTone={weekChange === null ? 'plain' : weekChange >= 0 ? 'up' : 'down'}
        />
        <MetricCard
          label="Resolution Rate"
          value={loading ? '—' : pct(resolutionRate)}
          icon="auto_awesome"
          iconTone="tertiary"
          badge={chat ? `${totals.closed.toLocaleString()} closed` : null}
          badgeTone="plain"
        />
        <MetricCard
          label="Total Sales from Chat"
          value={
            loading ? '—' : sales?.available ? money(sales.totalSalesFromChat, currency) : '—'
          }
          icon="payments"
          badge={
            sales?.available
              ? (changeBadge(sales.salesChangePct) ?? `${sales.windowDays}d window`)
              : null
          }
          badgeTone="highlight"
          highlight
        />
        <MetricCard
          label="Chat Conversion Rate"
          value={loading ? '—' : pct(chatConv)}
          icon="shopping_cart_checkout"
          iconTone="secondary"
          badge={sales?.available ? `${sales.attributedOrderCount} orders` : null}
          badgeTone="plain"
        />
      </div>

      {/* Main charts row */}
      <div className="cg-an-row">
        <section className="cg-panel">
          <div className="cg-panel-head">
            <div>
              <h2 className="cg-panel-title">Chat Volume Over Time</h2>
              <p className="cg-panel-sub">Customer messages vs. Daily volume</p>
            </div>
            <div className="cg-legend">
              <span className="cg-legend-item">
                <span
                  className="cg-legend-dot"
                  style={{ background: 'var(--cg-primary-container)' }}
                />
                Customer
              </span>
              <span className="cg-legend-item">
                <span
                  className="cg-legend-dot"
                  style={{ background: 'var(--cg-outline-variant)' }}
                />
                Daily Volume
              </span>
            </div>
          </div>
          <div className="cg-bars">
            {(chat?.volume ?? []).map((day) => (
              <div
                key={day.date}
                className="cg-bar-col"
                title={`${day.date}: ${day.customer} customer / ${day.total} total`}
              >
                <div
                  className="cg-bar-track"
                  style={{ height: `${Math.max(3, (day.total / maxVolume) * 100)}%` }}
                >
                  <div
                    className="cg-bar-fill"
                    style={{ height: day.total > 0 ? `${(day.customer / day.total) * 100}%` : 0 }}
                  />
                </div>
                <div className="cg-bar-day">{day.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="cg-panel">
          <div className="cg-panel-head">
            <div>
              <h2 className="cg-panel-title">Conversion Delta</h2>
              <p className="cg-panel-sub">Chat users vs. Site-wide</p>
            </div>
          </div>
          <div>
            <div className="cg-delta-item">
              <div className="cg-delta-toprow">
                <span className="cg-delta-tag">Chat Users</span>
                <span className="cg-delta-value">{pct(chatConv)}</span>
              </div>
              <div className="cg-delta-bar">
                <div
                  style={{
                    width: maxConv > 0 && chatConv !== null ? `${(chatConv / maxConv) * 90}%` : 0,
                  }}
                />
              </div>
            </div>
            <div className="cg-delta-item is-muted">
              <div className="cg-delta-toprow">
                <span className="cg-delta-tag">Site Average</span>
                <span className="cg-delta-value">{pct(siteConv)}</span>
              </div>
              <div className="cg-delta-bar">
                <div
                  style={{
                    width: maxConv > 0 && siteConv !== null ? `${(siteConv / maxConv) * 90}%` : 0,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="cg-panel-foot">
            <span className="cg-panel-foot-icon">
              <MaterialSymbol name="trending_up" size={16} />
            </span>
            <span>
              {convRatio !== null
                ? `${(Math.round(convRatio * 10) / 10).toLocaleString()}x ${
                    convRatio >= 1 ? 'higher' : 'lower'
                  } conversion for customers who chat.`
                : 'Conversion comparison appears once sales data is connected.'}
            </span>
          </div>
        </section>
      </div>

      {/* Insights row: AOV & cart recovery */}
      <div className="cg-an-row cg-an-row-halves">
        <section className="cg-card cg-aov-card">
          <div className="cg-watermark">
            <MaterialSymbol name="leaderboard" size={120} />
          </div>
          <div className="cg-section-label" style={{ marginBottom: 22 }}>
            Avg. Order Value (AOV)
          </div>
          <div className="cg-bignum-row">
            <div>
              <div className="cg-bignum-value">{money(aovChat, currency)}</div>
              <div className="cg-bignum-caption">Chat Attributed</div>
            </div>
            <div className="cg-vdivider" />
            <div>
              <div className="cg-bignum-value is-muted">{money(aovStore, currency)}</div>
              <div className="cg-bignum-caption is-muted">Store Overall</div>
            </div>
          </div>
          <div className="cg-uplift-row">
            {aovUplift !== null ? (
              <>
                <span className="cg-uplift-chip">
                  {aovUplift >= 0 ? '+' : ''}
                  {aovUplift}% {aovUplift >= 0 ? 'uplift' : 'gap'}
                </span>
                <span className="cg-uplift-note">per order average</span>
              </>
            ) : (
              <span className="cg-uplift-note">
                Appears once chat customers place orders.
              </span>
            )}
          </div>
        </section>

        <section className="cg-card cg-ring-card">
          <div className="cg-panel-head">
            <div>
              <h2 className="cg-panel-title">Cart Recovery via Chat</h2>
              <p className="cg-panel-sub">Abandoned cart rescues</p>
            </div>
            <span className="cg-window-chip">{sales?.windowDays ?? 60}d Window</span>
          </div>
          {recovery ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <div className="cg-ring-wrap">
                <svg width="84" height="84">
                  <circle
                    cx="42"
                    cy="42"
                    r="34"
                    fill="transparent"
                    stroke="rgba(198,196,217,0.2)"
                    strokeWidth="8"
                  />
                  <circle
                    cx="42"
                    cy="42"
                    r="34"
                    fill="transparent"
                    stroke="var(--cg-tertiary)"
                    strokeWidth="8"
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={ringOffset}
                  />
                </svg>
                <span className="cg-ring-value">{pct(recovery.rate)}</span>
              </div>
              <div className="cg-ring-row-group">
                <div className="cg-ring-row">
                  <span className="cg-ring-row-label">Total Rescued Revenue</span>
                  <span className="cg-ring-row-value">
                    {money(recovery.rescuedRevenue, currency)}
                  </span>
                </div>
                <div className="cg-ring-row">
                  <span className="cg-ring-row-label">Sessions Recovered</span>
                  <span className="cg-ring-row-value">
                    {recovery.sessionsRecovered.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="cg-panel-sub" style={{ margin: 0 }}>
              {sales?.available
                ? 'No abandoned checkouts from chat customers in this window.'
                : 'Appears once sales data is connected.'}
            </p>
          )}
        </section>
      </div>

      {/* Bento grid: common inquiries & top products */}
      <div className="cg-an-row cg-an-row-thirds">
        <section className="cg-card">
          <div className="cg-panel-head">
            <h2 className="cg-panel-title">Common Inquiries</h2>
          </div>
          {hasInquiryData ? (
            <div>
              {inquiries.map((inq, i) => (
                <div key={inq.label} className="cg-inq-row">
                  <span
                    className="cg-inq-dot"
                    style={{ background: INQUIRY_COLORS[i % INQUIRY_COLORS.length] }}
                  />
                  <div className="cg-inq-body">
                    <div className="cg-inq-toprow">
                      <span className="cg-inq-label">{inq.label}</span>
                      <span className="cg-inq-pct">{inq.pct}%</span>
                    </div>
                    <div className="cg-inq-bar">
                      <div
                        style={{
                          width: `${inq.pct}%`,
                          background: INQUIRY_COLORS[i % INQUIRY_COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="cg-panel-sub" style={{ margin: 0 }}>
              Topics appear as customer messages come in.
            </p>
          )}
        </section>

        <section className="cg-card">
          <div className="cg-panel-head">
            <h2 className="cg-panel-title">Top Products Sold via Chat</h2>
          </div>
          {topProducts.length > 0 ? (
            <div className="cg-products-grid">
              {topProducts.map((product) => (
                <div key={product.title} className="cg-product-card">
                  {product.imageUrl ? (
                    <img className="cg-product-img" src={product.imageUrl} alt={product.title} />
                  ) : (
                    <div
                      className="cg-product-img"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <MaterialSymbol name="inventory_2" size={28} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="cg-product-name">{product.title}</h3>
                    <div className="cg-product-stats">
                      <div>
                        <div className="cg-product-stat-label">Sales</div>
                        <div className="cg-product-stat-value">
                          {product.sales.toLocaleString()}
                        </div>
                      </div>
                      <div className="cg-product-divider" />
                      <div>
                        <div className="cg-product-stat-label">Revenue</div>
                        <div className="cg-product-stat-value">
                          {money(product.revenue, currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="cg-panel-sub" style={{ margin: 0 }}>
              {sales?.available
                ? 'Products appear here once chat customers place orders.'
                : 'Appears once sales data is connected.'}
            </p>
          )}
        </section>
      </div>
    </main>
  );
}
